import { Box, Button, Stack, Text } from '@ltvco/refresh-lib/theme';
import {
  EmojiObjectsOutlined,
  NavigateBefore,
  NavigateNext,
} from '@mui/icons-material';
import { useState } from 'react';

import {
  PatentCard,
  PatentCardBody,
  PatentCardDetail,
  PatentCardDetailLabel,
  PatentCardDetailValue,
  PatentCardGrid,
  PatentCardHeader,
  PatentCardStatCell,
  PatentCardStatsRow,
  PatentCardTitle,
  PatentIconCircle,
  StatCellLabel,
  StatCellValue,
} from './components';
import { PatentDetailsModal } from './PatentDetailsModal';
import { formatPatentDate } from './utils';

import type { PatentApplicationResult } from './types';

interface PatentResultCardProps {
  patent: PatentApplicationResult;
  onViewDetails: (_patent: PatentApplicationResult) => void;
}

const PatentResultCard = ({ patent, onViewDetails }: PatentResultCardProps) => {
  const meta = patent.applicationMetaData;
  const inventors = meta.inventorBag
    ?.map((inv) => inv.inventorNameText)
    .filter(Boolean)
    .join(', ');

  return (
    <PatentCard
      onClick={() => onViewDetails(patent)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onViewDetails(patent);
        }
      }}
    >
      <PatentCardHeader>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <PatentIconCircle>
            <EmojiObjectsOutlined sx={{ fontSize: '0.875rem' }} />
          </PatentIconCircle>
          <Text
            variant="caption"
            sx={{
              fontWeight: 700,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            {meta.applicationTypeLabelName || 'Patent'}
          </Text>
        </Stack>
      </PatentCardHeader>

      <PatentCardBody>
        <PatentCardTitle>
          {meta.inventionTitle || 'Untitled Application'}
        </PatentCardTitle>

        {inventors && (
          <PatentCardDetail>
            <PatentCardDetailLabel>Inventors:</PatentCardDetailLabel>
            <PatentCardDetailValue title={inventors}>
              {inventors}
            </PatentCardDetailValue>
          </PatentCardDetail>
        )}

        {meta.firstApplicantName && (
          <PatentCardDetail>
            <PatentCardDetailLabel>Applicant:</PatentCardDetailLabel>
            <PatentCardDetailValue title={meta.firstApplicantName}>
              {meta.firstApplicantName}
            </PatentCardDetailValue>
          </PatentCardDetail>
        )}
      </PatentCardBody>

      <PatentCardStatsRow>
        <PatentCardStatCell>
          <StatCellLabel>Filed Date</StatCellLabel>
          <StatCellValue>
            {meta.filingDate ? formatPatentDate(meta.filingDate) : '—'}
          </StatCellValue>
        </PatentCardStatCell>
        <PatentCardStatCell>
          <StatCellLabel>Grant Date</StatCellLabel>
          <StatCellValue>
            {meta.grantDate ? formatPatentDate(meta.grantDate) : '—'}
          </StatCellValue>
        </PatentCardStatCell>
      </PatentCardStatsRow>
    </PatentCard>
  );
};

export interface PatentResultsProps {
  results: PatentApplicationResult[];
  currentPage: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  isLoading: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;
}

export const PatentResults = ({
  results,
  currentPage,
  totalPages,
  hasNextPage,
  hasPreviousPage,
  isLoading,
  onNextPage,
  onPreviousPage,
}: PatentResultsProps) => {
  const [selectedPatent, setSelectedPatent] =
    useState<PatentApplicationResult | null>(null);

  return (
    <Box>
      <PatentCardGrid>
        {results.map((patent, index) => (
          <PatentResultCard
            key={patent.applicationNumberText || index}
            patent={patent}
            onViewDetails={setSelectedPatent}
          />
        ))}
      </PatentCardGrid>

      {totalPages > 1 && (
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={2}
          sx={{ mt: 3, mb: 2 }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<NavigateBefore />}
            disabled={!hasPreviousPage || isLoading}
            onClick={onPreviousPage}
          >
            Previous
          </Button>
          <Text variant="body2" color="text.secondary">
            Page {currentPage} of {totalPages}
          </Text>
          <Button
            variant="outlined"
            size="small"
            endIcon={<NavigateNext />}
            disabled={!hasNextPage || isLoading}
            onClick={onNextPage}
          >
            Next
          </Button>
        </Stack>
      )}

      <PatentDetailsModal
        patent={selectedPatent}
        open={!!selectedPatent}
        onClose={() => setSelectedPatent(null)}
      />
    </Box>
  );
};
